// Px3 Pipeline - the orders table.
//
// The screen an operator leaves open. It polls every 10s (the detail polls at
// 3s; a list of a hundred orders does not need that, and each refresh is a full
// table read server-side).
//
// The one thing this table is FOR: making `needs_attention` and
// `awaiting_capacity` impossible to miss. Both are honey, both print their
// reason in the row, and the filter bar puts them one click away. An order in
// either state is not progressing and nothing but an operator will move it.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Px3ShowToast } from '../Px3';
import {
  px3Api,
  px3ErrorMessage,
  type Px3Order,
  type Px3OrderStatus,
} from '../px3Api';

/** Same 10s as the other list screens in the app. */
const POLL_MS = 10_000;

/** Filter chips, in the order an operator scans them: the two that need a human
 *  first, then the ones in flight, then the finished ones. */
const FILTERS: { id: Px3OrderStatus; label: string }[] = [
  { id: 'needs_attention', label: 'Needs attention' },
  { id: 'awaiting_capacity', label: 'Awaiting capacity' },
  { id: 'running', label: 'Running' },
  { id: 'queued', label: 'Queued' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'dry_run_complete', label: 'Dry run' },
];

const STATUS_LABEL: Record<Px3OrderStatus, string> = {
  queued: 'queued',
  running: 'running',
  awaiting_capacity: 'awaiting capacity',
  needs_attention: 'needs attention',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
  dry_run_complete: 'dry run done',
};

/** Compact age. An order that has been `running` for four hours is a different
 *  animal from one that started four minutes ago, and the absolute timestamp
 *  makes the reader do that subtraction themselves. */
export function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '-';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d`;
}

export function OrdersList({
  onOpen,
  showToast,
}: {
  onOpen: (orderId: string) => void;
  showToast: Px3ShowToast;
}): JSX.Element {
  const [orders, setOrders] = useState<Px3Order[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Px3OrderStatus[]>([]);

  /** Monotonic request id, so a slow poll cannot overwrite a newer one - the
   *  filter changes mid-flight often enough for that to be a real race. */
  const reqId = useRef(0);

  /**
   * A failed load is NOT an empty list, and it is not a toast either. "No orders
   * yet." reads as "nothing is running", which is exactly the wrong conclusion
   * to draw about a pipeline that might have three parked builds in it, and an
   * 8s toast saying otherwise would be gone long before the operator acted. It
   * is state, it persists until a load succeeds, and `orders` keeps its last
   * good contents so a dropped poll does not blank a table that was on screen.
   *
   * Answers true only when THIS read landed and was still the newest one, so a
   * caller can wait for the result before it claims anything happened.
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    const mine = ++reqId.current;
    try {
      const fresh = await px3Api.listOrders({ ...(filter.length ? { status: filter } : {}) });
      if (mine !== reqId.current) return false;
      setOrders(fresh);
      setListError(null);
      return true;
    } catch (err) {
      if (mine !== reqId.current) return false;
      setListError(px3ErrorMessage(err));
      return false;
    } finally {
      if (mine === reqId.current) setLoaded(true);
    }
  }, [filter]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  function toggleFilter(status: Px3OrderStatus): void {
    setLoaded(false);
    setFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  }

  const attention = orders.filter(
    (o) => o.status === 'needs_attention' || o.status === 'awaiting_capacity',
  ).length;

  return (
    <>
      {listError && (
        <div className="px3-toast px3-toast--error">Could not load the order list: {listError}</div>
      )}

      <section className="px3-card">
        <div className="px3-card-header">
          <h2 className="px3-card-title">
            Orders
            {attention > 0 && (
              <span className="px3-badge px3-badge--warn" style={{ marginLeft: 10 }}>
                {attention} waiting on you
              </span>
            )}
          </h2>
          <div className="px3-actions">
            {/* The toast waits for the read: firing it beside the request said
                "refreshed" while the request was still in the air, and said it
                again on the reads that came back 500. */}
            <button
              type="button"
              className="px3-btn px3-btn--ghost px3-btn--small"
              onClick={() => {
                void refresh().then((ok) => {
                  if (ok) showToast('info', 'Order list refreshed.');
                });
              }}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="px3-card-body">
          <div className="px3-actions">
            <button
              type="button"
              className={`px3-subnav-item ${filter.length === 0 ? 'active' : ''}`}
              onClick={() => {
                setLoaded(false);
                setFilter([]);
              }}
            >
              All
            </button>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`px3-subnav-item ${filter.includes(f.id) ? 'active' : ''}`}
                onClick={() => toggleFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px3-table-wrap">
          <table className="px3-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>External ref</th>
                <th>Source</th>
                <th className="px3-col-center">Status</th>
                <th className="px3-col-center">Stage</th>
                <th className="px3-col-center">Domains</th>
                <th className="px3-col-center">Mailboxes</th>
                <th className="px3-col-center">Age</th>
                <th>Attention / error</th>
                <th className="px3-col-center" />
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px3-empty">
                    {!loaded
                      ? 'Loading orders...'
                      : listError !== null
                        ? 'The order list could not be loaded - this is not an empty list. Use Refresh to try again.'
                        : filter.length > 0
                          ? 'No orders in those statuses. Clear the filter to see the rest.'
                          : 'No orders yet. A portal POSTs them to /api/px3/orders, or use Submit to send one from here.'}
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  // No row-level onClick. A clickable <tr> is mouse-only unless
                  // it grows tabIndex + role + a key handler, and the row already
                  // carries a real Open button a keyboard reaches for free.
                  <tr
                    key={order.id}
                    className={
                      order.status === 'failed' || order.status === 'cancelled' ? 'px3-row--bad' : ''
                    }
                  >
                    <td>
                      {order.customer}
                      {order.dry_run && (
                        <span className="px3-badge px3-badge--muted" style={{ marginLeft: 8 }}>
                          dry run
                        </span>
                      )}
                    </td>
                    <td className="px3-cell-mono">{order.external_ref}</td>
                    <td className="px3-cell-faint">{order.source_name ?? 'admin'}</td>
                    <td className="px3-col-center">
                      <span className={`px3-badge px3-badge--${order.status}`}>
                        {STATUS_LABEL[order.status]}
                      </span>
                    </td>
                    <td className="px3-col-center px3-cell-mono">{order.stage}</td>
                    <td className="px3-col-center px3-cell-faint">{order.domains_total}</td>
                    {/* Progress, not just size: "12/300" is the number an
                        operator scanning the list is actually after, and the
                        failed count beside it is why a row is parked. Both are
                        computed by the LIST endpoint only, so the render falls
                        back to the plain total when they are absent rather than
                        printing a zero it did not measure. */}
                    <td className="px3-col-center px3-cell-faint">
                      {order.mailboxes_done !== undefined
                        ? `${order.mailboxes_done}/${order.mailboxes_total}`
                        : order.mailboxes_total}
                      {order.mailboxes_failed !== undefined && order.mailboxes_failed > 0 && (
                        <span className="px3-badge px3-badge--error" style={{ marginLeft: 6 }}>
                          {order.mailboxes_failed} failed
                        </span>
                      )}
                      <span className="px3-cell-faint"> ({order.users_per_domain}/domain)</span>
                    </td>
                    <td className="px3-col-center px3-cell-faint" title={order.created_at}>
                      {ago(order.created_at)}
                    </td>
                    <td className={order.error_code ? 'px3-cell-error' : 'px3-cell-faint'}>
                      {order.attention_reason ??
                        (order.error_code
                          ? `${order.error_code}${order.error_message ? `: ${order.error_message}` : ''}`
                          : '-')}
                    </td>
                    <td className="px3-col-center">
                      <div className="px3-rowactions">
                        <button
                          type="button"
                          className="px3-btn px3-btn--secondary px3-btn--small"
                          onClick={() => onOpen(order.id)}
                        >
                          Open
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
